/**
 * The wall clock, as an external store.
 *
 * The bidding countdown has to re-render once a second, and the current time is exactly the
 * kind of value a React component may not read during render: it changes on its own, so
 * reading it mid-render makes the output depend on when the render happened. Modelling the
 * clock as a subscribable store keeps `useCountdown` pure — it reads a snapshot React
 * controls, and the store is what talks to `Date.now()`.
 *
 * One interval serves every countdown on the desk, and it runs only while something is
 * subscribed: a desk with no auction panel mounted keeps no timer alive.
 *
 * Identical to `dealbid-web`'s copy on purpose. Canon § 8 gives Dealbid one clock, and two
 * surfaces that ticked differently would disagree about when bidding closes.
 */

const TICK_MS = 1000;

let nowMs = Date.now();
let timer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

function tick(): void {
  nowMs = Date.now();
  for (const listener of listeners) listener();
}

export function subscribeToClock(listener: () => void): () => void {
  listeners.add(listener);
  if (timer === null) {
    // Refreshed on the first subscription so a countdown mounted long after page load does
    // not start from a snapshot taken at import time.
    nowMs = Date.now();
    timer = setInterval(tick, TICK_MS);
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };
}

/** The cached instant. Stable between ticks, which is what makes it safe to read in render. */
export function clockSnapshot(): number {
  return nowMs;
}
